import { apiService } from "./api";

export interface InventoryCategory {
  _id: string;
  companyId: string;
  title: string;
  description?: string;
  itemCount?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface InventoryItem {
  _id: string;
  companyId: string;
  categoryId:
    | string
    | {
        _id: string;
        title: string;
      };
  title: string;
  description?: string;
  price: number;
  quantity: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface InventoryHistoryFieldChange {
  from: string | number | boolean | null;
  to: string | number | boolean | null;
}

export interface InventoryHistoryEntry {
  _id: string;
  companyId: string;
  itemId:
    | string
    | {
        _id: string;
        title: string;
      };
  categoryId:
    | string
    | {
        _id: string;
        title: string;
      };
  action: "add" | "use" | "adjust" | "edit" | "correction_add" | "correction_deduct";
  quantity: number;
  inventoryDelta: number;
  previousQuantity: number;
  newQuantity: number;
  cost: number;
  priceSnapshot: number;
  priceOverride: number;
  note?: string;
  fieldChanges?:
    | Partial<
        Record<
          "title" | "description" | "category" | "price" | "isActive",
          InventoryHistoryFieldChange
        >
      >
    | null;
  itemTitleSnapshot?: string;
  maintenanceId?:
    | string
    | {
        _id: string;
        requestId: string;
        title: string;
        status: string;
      }
    | null;
  expenseId?:
    | string
    | {
        _id: string;
        amount: number;
        status: string;
        subtype: string;
        description: string;
      }
    | null;
  performedBy?: {
    userId?: string;
    companyUserId?: string;
    name?: string;
    role?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface InventoryListResponse<T> {
  success: boolean;
  count: number;
  data: T[];
}

export interface InventoryPaginatedResponse<T> extends InventoryListResponse<T> {
  totalCount: number;
  pagination: {
    page: number;
    limit: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface InventoryApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

export interface InventoryItemFilters {
  companyId: string;
  categoryId?: string;
  search?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
}

export interface InventoryCategoryFilters {
  companyId: string;
  isActive?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

export interface InventoryHistoryFilters {
  companyId: string;
  itemId?: string;
  categoryId?: string;
  maintenanceId?: string;
  action?: string;
  search?: string;
  page?: number;
  limit?: number;
}

function buildQuery(
  params: Record<string, string | number | boolean | undefined | null>
) {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.append(key, String(value));
    }
  });

  return query.toString();
}

export const inventoryService = {
  async getCategories(filters: InventoryCategoryFilters) {
    const query = buildQuery({ ...filters });
    const response = await apiService.get<InventoryPaginatedResponse<InventoryCategory>>(
      `/inventory/categories?${query}`
    );
    return response.data;
  },

  async createCategory(data: {
    companyId: string;
    title: string;
    description?: string;
    isActive?: boolean;
  }) {
    const response = await apiService.post<InventoryApiResponse<InventoryCategory>>(
      "/inventory/categories",
      data
    );
    return response.data;
  },

  async updateCategory(
    id: string,
    data: { companyId: string; title?: string; description?: string; isActive?: boolean }
  ) {
    const response = await apiService.put<InventoryApiResponse<InventoryCategory>>(
      `/inventory/categories/${id}`,
      data
    );
    return response.data;
  },

  async deleteCategory(id: string, companyId: string) {
    const response = await apiService.delete<InventoryApiResponse<{ id: string }>>(
      `/inventory/categories/${id}`,
      {
        data: { companyId },
      }
    );
    return response.data;
  },

  async getItems(filters: InventoryItemFilters) {
    const query = buildQuery({ ...filters });
    const response = await apiService.get<InventoryPaginatedResponse<InventoryItem>>(
      `/inventory/items?${query}`
    );
    return response.data;
  },

  async getLimitedItems(
    companyId: string,
    options?: {
      withPrice?: boolean;
      withQuantity?: boolean;
      categoryId?: string;
      search?: string;
      includeInactive?: boolean;
    }
  ) {
    const query = buildQuery({ companyId, ...options });
    const response = await apiService.get<InventoryListResponse<InventoryItem>>(
      `/inventory/items/limited?${query}`
    );
    return response.data;
  },

  async createItem(data: {
    companyId: string;
    categoryId: string;
    title: string;
    description?: string;
    price?: number;
    quantity?: number;
    initialCost?: number;
    isActive?: boolean;
  }) {
    const response = await apiService.post<InventoryApiResponse<InventoryItem>>(
      "/inventory/items",
      data
    );
    return response.data;
  },

  async updateItem(
    id: string,
    data: {
      companyId: string;
      categoryId?: string;
      title?: string;
      description?: string;
      price?: number;
      quantity?: number;
      isActive?: boolean;
    }
  ) {
    const response = await apiService.put<InventoryApiResponse<InventoryItem>>(
      `/inventory/items/${id}`,
      data
    );
    return response.data;
  },

  async deleteItem(id: string, companyId: string) {
    const response = await apiService.delete<InventoryApiResponse<{ id: string }>>(
      `/inventory/items/${id}`,
      {
        data: { companyId },
      }
    );
    return response.data;
  },

  async addStock(
    id: string,
    data: { companyId: string; quantity: number; cost?: number; note?: string }
  ) {
    const response = await apiService.post<InventoryApiResponse<InventoryHistoryEntry>>(
      `/inventory/items/${id}/add-stock`,
      data
    );
    return response.data;
  },

  async useItem(
    id: string,
    data: {
      companyId: string;
      quantity: number;
      maintenanceId?: string;
      note?: string;
      priceOverride?: number;
    }
  ) {
    const response = await apiService.post<InventoryApiResponse<InventoryHistoryEntry>>(
      `/inventory/items/${id}/use`,
      data
    );
    return response.data;
  },

  async getHistory(filters: InventoryHistoryFilters) {
    const query = buildQuery({ ...filters });
    const response = await apiService.get<InventoryPaginatedResponse<InventoryHistoryEntry>>(
      `/inventory/history?${query}`
    );
    return response.data;
  },
};

export default inventoryService;
